import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { createApiClient, type ApiClient } from './client.js';
import { createClassroomsApi, type ClassroomsApi } from './classrooms.js';
import { createPreferencesApi, type PreferencesApi } from './preferences.js';
import { createHomeApi, type HomeApi } from './home.js';
import { createCalendarApi, type CalendarApi } from './calendar.js';
import { createDayOfSchoolApi, type DayOfSchoolApi } from './dayOfSchool.js';
import { createWeatherApi, type WeatherApi } from './weather.js';
import { createPlannerApi, type PlannerApi } from './planner.js';
import { createDayHistoryApi, type DayHistoryApi } from './dayHistory.js';
import { createAdminApi, type AdminApi } from './admin.js';
import { createYoutubeApi, type YoutubeApi } from './youtube.js';
import { createLayoutApi, type LayoutApi } from './layout.js';
import { createLinksApi, type LinksApi } from './links.js';
import { createTechTimeApi, type TechTimeApi } from './techTime.js';

export interface ClassyApi {
  client: ApiClient;
  classrooms: ClassroomsApi;
  preferences: PreferencesApi;
  home: HomeApi;
  calendar: CalendarApi;
  dayOfSchool: DayOfSchoolApi;
  weather: WeatherApi;
  planner: PlannerApi;
  dayHistory: DayHistoryApi;
  admin: AdminApi;
  youtube: YoutubeApi;
  layout: LayoutApi;
  links: LinksApi;
  techTime: TechTimeApi;
}

const ApiContext = createContext<ClassyApi | null>(null);

export function useApi(): ClassyApi {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used inside <ApiProvider>');
  return ctx;
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const value = useMemo<ClassyApi>(() => {
    const client = createApiClient({ getToken: auth.getToken });
    return {
      client,
      classrooms: createClassroomsApi(client),
      preferences: createPreferencesApi(client),
      home: createHomeApi(client),
      calendar: createCalendarApi(client),
      dayOfSchool: createDayOfSchoolApi(client),
      weather: createWeatherApi(client),
      planner: createPlannerApi(client),
      dayHistory: createDayHistoryApi(client),
      admin: createAdminApi(client),
      youtube: createYoutubeApi(client),
      layout: createLayoutApi(client),
      links: createLinksApi(client),
      techTime: createTechTimeApi(client),
    };
  }, [auth.getToken]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export const ApiContextProvider = ApiContext.Provider;
